import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { isLehrkraft, Taetigkeiten } from '@martinus/taetigkeit/enums/Taetigkeiten.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { Sachkontenzuordnung } from '../../../../db/static-data/sachkontenzuordnung.js';
import { calculateWorkingHoursForEingruppierung } from '../../../api/calculateWorkingHours.js';
import { getHistoryForPath } from '../../../api/getHistoryForPath.js';
import { getReadableHistoryStrings } from '../../../api/getReadableHistoryStrings.js';
import { FULL_TIME_WORKING_HOURS } from '../../../constants/fullTimeWorkingHours.js';
import { MaxDeputatsStunden } from '../../../constants/maxDeputatStunden.js';
import { type AppliedContract, ContractSupplementApplyHistoryForPath } from '../../../entities/AppliedContract.js';
import { ContractSupplementChange, ContractSupplementDataType } from '../../../entities/ContractSupplement.js';
import {
  AGEingruppierung1,
  AGEingruppierung2,
} from '../../../entities/vertrag-sektionen/vertrag-eingruppierung/AGEingruppierung.js';
import { Zahlung } from '../../../entities/vertrag-sektionen/Zahlung.js';
import { Entgeltart } from '../../../enums/Entgeltart.js';
import { Lohnart } from '../../../enums/Lohnart.js';
import { Months } from '../../../enums/Months.js';
import { Tarifgruppe } from '../../../enums/Tarifgruppe.js';
import {
  type ContractForm,
  type EingruppierungPath,
  ErfahrungsstufeText,
  KostenstellenEditor,
  TaetigkeitsschluesselDisplay,
} from './sharedContractEditParts.js';

interface ContractEditEingruppierungSectionProps {
  form: ContractForm;
  contract: Maybe<AppliedContract>;
  suffix: '1' | '2';
  hasEditPermission: boolean;
  /** whether the fields are currently editable, see {@link KostenstellenEditor} */
  canEdit: boolean;
  onChanged: () => void;
}

/** builds the initial history entry for zahlungen fields, as they get created as complete objects in supplements */
const buildInitalHistoryForZahlungen = (
  contract: Maybe<AppliedContract>,
  path: EingruppierungPath,
  index: number,
  initialHistory: any,
  key: string,
) => {
  const betragHistoryChanges: ContractSupplementChange[] = [];
  const betragHistory = getHistoryForPath(
    contract?.history,
    `${path}.agLohnGehaltszahlungen.monatlicheZahlungen.${index}.${key}`,
  );
  if (betragHistory?.changes?.length) {
    betragHistoryChanges.push(...betragHistory.changes);
  }

  if (initialHistory) {
    if (!betragHistory?.changes?.length) {
      betragHistoryChanges.unshift(
        new ContractSupplementChange({ value: initialHistory.betrag, type: ContractSupplementDataType.STRING }),
      );
    }

    betragHistoryChanges.unshift(
      new ContractSupplementChange({ value: 'null', type: ContractSupplementDataType.NULL }),
    );
  }

  return new ContractSupplementApplyHistoryForPath({ changes: betragHistoryChanges });
};

const ZahlungenEditor = (props: ContractEditEingruppierungSectionProps) => {
  const path: EingruppierungPath = `agEingruppierung${props.suffix}`;
  const lohnGehaltszahlungen = props.form.document?.[path]?.agLohnGehaltszahlungen;

  return html`
    <div class="flex flex-col gap-2">
      ${repeat(
        lohnGehaltszahlungen?.monatlicheZahlungen ?? [],
        zahlung => zahlung._id,
        (zahlung, index) => {
          // additional history for object creation. it is relatively complex in the supplement as we old objects as new values. Any additional changes will be mnade by the standard system
          const initialHistory = getHistoryForPath(
            props.contract?.history,
            `${path}.agLohnGehaltszahlungen.monatlicheZahlungen`,
          )
            ?.changes?.filter(c => c.type === ContractSupplementDataType.JSON)
            .map(c => JSON.parse(c.value))
            .filter(v => v.length > 0)
            .find(changeArray => changeArray.some((z: any) => z._id === zahlung._id))
            ?.find((z: any) => z._id === zahlung._id);

          const historyWithInitalValuesLohnart = buildInitalHistoryForZahlungen(
            props.contract,
            path,
            index,
            initialHistory,
            'lohnart',
          );

          const historyWithInitalValuesBetrag = buildInitalHistoryForZahlungen(
            props.contract,
            path,
            index,
            initialHistory,
            'betrag',
          );

          return html`
            <div class="card p-3 gap-2">
              <div class="flex flex-row items-center justify-between">
                <span class="text-sm text-base-content/70">Zahlung ${index + 1}</span>
                ${
                  props.hasEditPermission
                    ? html`
                        <button
                          class="btn btn-ghost btn-sm btn-square"
                          aria-label="Zahlung löschen"
                          @click=${() => {
                            lohnGehaltszahlungen?.monatlicheZahlungen?.splice(index, 1);
                            props.onChanged();
                          }}
                        >
                          <span class="icon-[material-symbols--delete-outline] text-error text-lg"></span>
                        </button>
                      `
                    : nothing
                }
              </div>

              <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr] items-center gap-2">
                ${FormGridEntry({
                  label: 'Lohnart',
                  render: DropdownSelectionInput({
                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'monatlicheZahlungen', index, 'lohnart'),
                    selectables: Object.values(Lohnart),
                    placeholder: 'Lohnart',
                    clearable: true,
                  }),
                  history: getReadableHistoryStrings(historyWithInitalValuesLohnart),
                })}
                ${FormGridEntry({
                  label: 'Betrag',
                  render: NumberInput({
                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'monatlicheZahlungen', index, 'betrag'),
                    step: 0.01,
                    min: 0,
                    placeholder: '€',
                    placeholderMode: 'prefix',
                  }),
                  history: getReadableHistoryStrings(historyWithInitalValuesBetrag),
                })}
                ${FormGridEntry({
                  label: 'Monat der Zahlung',
                  labelAddition: html`
                    <div class="tooltip" ${hoverTooltip()}>
                      <div class="tooltip-content max-w-[600px] text-left">
                        Beschreibt in welchem Monat die Zahlung wiederkehrend ist. Zum Beispiel immer im November. Wird
                        dieses Feld leer gelassen geht das System davon aus, dass die Zahlung jeden Monat ausgezahlt
                        werden soll.
                      </div>
                      <span class="icon-[material-symbols--info-outline] text-base-content/70"></span>
                    </div>
                  `,
                  render: DropdownSelectionInput({
                    clearable: true,
                    placeholder: 'Monat der Zahlung',
                    selectables: Object.values(Months),
                    value: !isNullish(zahlung.monat) ? Object.values(Months)[zahlung.monat] : undefined,
                    onPicked: (value: Months | null) => {
                      if (!value) {
                        zahlung.monat = null;
                        props.onChanged();
                        return;
                      }

                      zahlung.monat = Object.values(Months).findIndex(v => v === value);
                      props.onChanged();
                    },
                  }),
                  history: getReadableHistoryStrings(
                    getHistoryForPath(
                      props.contract?.history,
                      `${path}.agLohnGehaltszahlungen.monatlicheZahlungen.${index}.monat`,
                    ),
                  ),
                })}
                ${FormGridEntry({
                  label: 'Jahr',
                  labelAddition: html`
                    <div class="tooltip" ${hoverTooltip()}>
                      <div class="tooltip-content max-w-[600px] text-left">
                        Beschreibt in welchem Jahr die Zahlung ausgezahlt werden soll. Dadurch kann zum Beispiel jeden
                        Monat in einem Jahr, oder zusammen mit einer Auswahl des Monats, eine einmalige Zahlung erstellt
                        werden.
                      </div>
                      <span class="icon-[material-symbols--info-outline] text-base-content/70"></span>
                    </div>
                  `,
                  render: DropdownSelectionInput({
                    ...props.form.field(path, 'agLohnGehaltszahlungen', 'monatlicheZahlungen', index, 'jahr'),
                    clearable: true,
                    placeholder: 'Jahr der Zahlung',
                    selectables: new Array(8).fill(0).map((_, i) => new Date().getFullYear() - 1 + i),
                  }),
                  history: getReadableHistoryStrings(
                    getHistoryForPath(
                      props.contract?.history,
                      `${path}.agLohnGehaltszahlungen.monatlicheZahlungen.${index}.jahr`,
                    ),
                  ),
                })}
              </div>
            </div>
          `;
        },
      )}
      ${
        props.hasEditPermission
          ? html`
              <button
                class="btn btn-dash btn-block justify-start"
                @click=${() => {
                  const lohn = props.form.document?.[path]?.agLohnGehaltszahlungen;
                  if (!lohn) return;
                  lohn.monatlicheZahlungen ??= [];
                  lohn.monatlicheZahlungen.push(new Zahlung({}));
                  props.onChanged();
                }}
              >
                <span class="icon-[material-symbols--add]"></span>
                Zahlung hinzufügen
              </button>
            `
          : nothing
      }
    </div>
  `;
};

/** One complete Eingruppierung of the expert contract edit */
export const ContractEditEingruppierungSection = stateful((props: ContractEditEingruppierungSectionProps) => {
  const path: EingruppierungPath = `agEingruppierung${props.suffix}`;

  const historyFor = (subPath: string) =>
    getReadableHistoryStrings(getHistoryForPath(props.contract?.history, `${path}.${subPath}`));

  const eingruppierung = props.form.document?.[path];

  if (!props.hasEditPermission && !eingruppierung) return nothing;

  const taetigkeit = eingruppierung?.agVertragsInfos?.taetigkeit;
  const isLehrer = isLehrkraft(taetigkeit);

  const appliedStart = props.form.document?.[`appliedSupplementStartAG${props.suffix}`];
  const appliedEnd = props.form.document?.[`appliedSupplementEndAG${props.suffix}`];

  return html`
    <div class="flex flex-col gap-3">
      ${CardHeader({
        title: `Eingruppierung ${props.suffix}`,
        actionsDivider: true,
        actions:
          props.suffix === '2' && props.form.document?.agEingruppierung2 && props.hasEditPermission
            ? html`
                <button
                  class="btn btn-ghost btn-sm btn-square"
                  aria-label="Eingruppierung 2 löschen"
                  @click=${() => {
                    if (!props.form.document) return;
                    props.form.document.agEingruppierung2 = null;
                    props.onChanged();
                  }}
                >
                  <span class="icon-[material-symbols--delete-outline] text-error text-lg"></span>
                </button>
              `
            : nothing,
      })}
      ${
        !eingruppierung && props.hasEditPermission
          ? html`
              <button
                type="button"
                class="tooltip btn btn-dash btn-block justify-start"
                ${hoverTooltip()}
                @click=${() => {
                  if (!props.form.document) return;
                  props.form.document[path] =
                    props.suffix === '1' ? new AGEingruppierung1({}) : new AGEingruppierung2({});
                  props.onChanged();
                }}
              >
                <div class="tooltip-content max-w-[600px] text-left">
                  Wenn eine neue Eingruppierung hinzugefügt wird, wird diese per Nachtrag hinzugefügt. Die zweite
                  Eingruppierung beinhaltet eine komplett eigenständige Tätigkeit.
                </div>
                <span class="icon-[material-symbols--add]"></span>
                Eingruppierung hinzufügen
              </button>
            `
          : html`
              ${Collapse({
                className: 'border-0 bg-transparent shadow-none',
                heading: html`<h4 class="text-base font-semibold">Allgemeine Eingruppierungsinformationen</h4>`,
                defaultOpen: true,
                content: html`
                  <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
                    ${FormGridEntry({
                      label: 'Gültig ab',
                      render: appliedStart
                        ? html`
                            <p>
                              ${
                                appliedStart
                                  ? formatCalendarDay(appliedStart, 'dd.MM.yyyy')
                                  : props.form.document?.agInfos?.einstellungsdatum
                                    ? formatCalendarDay(props.form.document.agInfos.einstellungsdatum, 'dd.MM.yyyy')
                                    : ''
                              }
                            </p>
                          `
                        : DateInput({
                            disabled: !props.hasEditPermission,
                            clearable: true,
                            placeholder: 'Wann tritt die Eingruppierung in Kraft?',
                            placeholderMode: 'hide',
                            value:
                              eingruppierung?.agVertragsInfos?.startDate ??
                              props.form.document?.agInfos?.einstellungsdatum ??
                              undefined,
                            onPicked: (date: Date | null) => {
                              if (!eingruppierung?.agVertragsInfos) return;
                              eingruppierung.agVertragsInfos.startDate = date;
                              props.onChanged();
                            },
                          }),
                    })}
                    ${FormGridEntry({
                      label: 'Gültig bis',
                      render:
                        appliedEnd || appliedStart
                          ? html`<p>${appliedEnd ? formatCalendarDay(appliedEnd, 'dd.MM.yyyy') : 'Offenes Ende'}</p>`
                          : DateInput({
                              disabled: !props.hasEditPermission,
                              clearable: true,
                              placeholder: 'Offenes Ende',
                              placeholderMode: 'hide',
                              value:
                                eingruppierung?.agVertragsInfos?.endDate ??
                                props.form.document?.agInfos?.gekuendigtZum ??
                                undefined,
                              onPicked: (date: Date | null) => {
                                if (!eingruppierung?.agVertragsInfos) return;
                                eingruppierung.agVertragsInfos.endDate = date;
                                props.onChanged();
                              },
                            }),
                    })}

                    <hr class="col-span-full border-base-300" />

                    ${FormGridEntry({
                      label: 'Tätigkeit',
                      render: DropdownSelectionInput({
                        ...props.form.field(path, 'agVertragsInfos', 'taetigkeit'),
                        placeholder: 'Tätigkeit',
                        clearable: true,
                        selectables: Object.values(Taetigkeiten),
                        onPicked: (value: Taetigkeiten | null) => {
                          props.form.field(path, 'agVertragsInfos', 'taetigkeit').onPicked?.(value);

                          const arbeitszeit = props.form.document?.[path]?.agArbeitszeit;
                          if (arbeitszeit) {
                            arbeitszeit.stunden = null;
                            arbeitszeit.maxDeputat = null;

                            if (!isLehrkraft(value)) {
                              arbeitszeit.stunden = FULL_TIME_WORKING_HOURS;
                            }
                          }
                          props.onChanged();
                        },
                      }),
                      history: historyFor('agVertragsInfos.taetigkeit'),
                    })}
                    ${FormGridEntry({
                      label: 'Tätigkeitsschlüssel',
                      render: TaetigkeitsschluesselDisplay(eingruppierung?.agVertragsInfos?.taetigkeitsschluessel),
                      history: historyFor('agVertragsInfos.taetigkeitsschluessel'),
                    })}
                    ${FormGridEntry({
                      label: 'Zeitarbeitsverhältnis, Arbeitnehmerüberlassung',
                      render: ToggleInput({
                        ...props.form.field(path, 'agVertragsInfos', 'zeitarbeitsverhaeltnis'),
                        label: eingruppierung?.agVertragsInfos?.zeitarbeitsverhaeltnis ? 'Ja' : 'Nein',
                      }),
                      history: historyFor('agVertragsInfos.zeitarbeitsverhaeltnis'),
                    })}
                    ${FormGridEntry({
                      label: 'Stellenbezeichnung / Tätigkeit',
                      render: TextInput({
                        ...props.form.field(path, 'agVertragsInfos', 'stellenbezeichnung'),
                        placeholder: 'Keine Stellenbezeichnung',
                        placeholderMode: 'hide',
                      }),
                      history: historyFor('agVertragsInfos.stellenbezeichnung'),
                    })}
                    ${FormGridEntry({
                      label: 'Sachkontenzuordnung',
                      render: DropdownSelectionInput({
                        ...props.form.field(path, 'agVertragsInfos', 'sachkontenzuordnung'),
                        selectables: Object.values(Sachkontenzuordnung).map(skz => skz.Bezeichnung),
                        placeholder: 'Keine Sachkontenzuordnung',
                        clearable: true,
                      }),
                      history: historyFor('agVertragsInfos.sachkontenzuordnung'),
                    })}
                  </div>
                `,
              })}

              <hr class="border-base-300" />

              ${Collapse({
                className: 'border-0 bg-transparent shadow-none',
                heading: html`<h4 class="text-base font-semibold">Arbeitszeit</h4>`,
                defaultOpen: true,
                content: html`
                  <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
                    ${FormGridEntry({
                      label: 'Stunden pro Woche',
                      render: NumberInput({
                        ...props.form.field(path, 'agArbeitszeit', 'zaehler'),
                        placeholder: 'Keine Stunden pro Woche hinterlegt',
                        placeholderMode: 'hide',
                        clearable: true,
                        min: 0,
                      }),
                      history: historyFor('agArbeitszeit.zaehler'),
                    })}
                    ${
                      isLehrer
                        ? FormGridEntry({
                            label: 'Maximales Deputat',
                            render: DropdownSelectionInput({
                              ...props.form.field(path, 'agArbeitszeit', 'maxDeputat'),
                              placeholder: 'Kein maximales Deputat',
                              selectables: Object.values(MaxDeputatsStunden).filter(
                                v => taetigkeit !== Taetigkeiten.LEHRKRAFT || (v !== 33 && v !== 30 && v !== 39),
                              ) as number[],
                              clearable: true,
                              onPicked: (value: number | null) => {
                                props.form.field(path, 'agArbeitszeit', 'maxDeputat').onPicked?.(value);
                                if (!isNullish(value) && props.form.document?.[path]?.agArbeitszeit) {
                                  props.form.document[path].agArbeitszeit.stunden = null;
                                }
                                props.onChanged();
                              },
                            }),
                            history: historyFor('agArbeitszeit.maxDeputat'),
                          })
                        : FormGridEntry({
                            label: 'Stunden',
                            render: NumberInput({
                              ...props.form.field(path, 'agArbeitszeit', 'stunden'),
                              placeholder: '(nicht-Lehrkräfte)',
                              placeholderMode: 'hide',
                              clearable: true,
                              min: 0,
                              onPicked: (value: number | null) => {
                                props.form.field(path, 'agArbeitszeit', 'stunden').onPicked?.(value);
                                if (!isNullish(value) && props.form.document?.[path]?.agArbeitszeit) {
                                  props.form.document[path].agArbeitszeit.maxDeputat = null;
                                }
                                props.onChanged();
                              },
                            }),
                            history: historyFor('agArbeitszeit.stunden'),
                          })
                    }
                    ${FormGridEntry({
                      label: 'Ferien einarbeiten?',
                      render: ToggleInput({
                        ...props.form.field(path, 'agArbeitszeit', 'ferienEinarbeiten'),
                        label: eingruppierung?.agArbeitszeit?.ferienEinarbeiten ? 'Ja' : 'Nein',
                        onPicked: (value: boolean | null) => {
                          props.form.field(path, 'agArbeitszeit', 'ferienEinarbeiten').onPicked?.(value);
                          props.onChanged();
                        },
                      }),
                      history: historyFor('agArbeitszeit.ferienEinarbeiten'),
                    })}
                    ${FormGridEntry({
                      label: 'Wöchentliche Arbeitszeit',
                      render: html`
                        <p>
                          <b>
                            ${eingruppierung ? calculateWorkingHoursForEingruppierung(eingruppierung).toFixed(2) : 0}
                          </b>
                          Stunden
                        </p>
                      `,
                    })}
                  </div>
                `,
              })}

              <hr class="border-base-300" />

              <div class="grid grid-cols-1 md:grid-cols-[1fr_5fr] items-start gap-2">
                <span class="text-sm font-semibold text-base-content/70">Kostenstellen</span>
                ${KostenstellenEditor({
                  form: props.form,
                  contract: props.contract,
                  path,
                  hasEditPermission: props.hasEditPermission,
                  canEdit: props.canEdit,
                  onChanged: props.onChanged,
                })}
              </div>

              <hr class="border-base-300" />

              ${Collapse({
                className: 'border-0 bg-transparent shadow-none',
                heading: html`<h4 class="text-base font-semibold">Lohn und Gehaltszahlung</h4>`,
                defaultOpen: true,
                content: html`
                  <div class="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_2fr] items-center gap-2">
                    ${FormGridEntry({
                      label: 'Entgeltart',
                      render: DropdownSelectionInput({
                        ...props.form.field(path, 'agLohnGehaltszahlungen', 'entgeltart'),
                        selectables: Object.values(Entgeltart),
                        placeholder: 'Keine Entgeltart',
                        clearable: true,
                      }),
                      history: historyFor('agLohnGehaltszahlungen.entgeltart'),
                    })}
                    ${FormGridEntry({
                      label: 'Tarifgruppe',
                      render: DropdownSelectionInput({
                        ...props.form.field(path, 'agLohnGehaltszahlungen', 'tarifgruppe'),
                        selectables: Object.values(Tarifgruppe),
                        placeholder: 'Keine Tarifgruppe',
                        clearable: true,
                        onPicked: (value: Tarifgruppe | null) => {
                          props.form.field(path, 'agLohnGehaltszahlungen', 'tarifgruppe').onPicked?.(value);
                          props.onChanged();
                        },
                      }),
                      history: historyFor('agLohnGehaltszahlungen.tarifgruppe'),
                    })}
                    ${FormGridEntry({
                      label: 'Fiktives Datum für Beginn Stufe 1 bei höherer Eingruppierung',
                      render: DateInput({
                        ...props.form.field(path, 'agLohnGehaltszahlungen', 'fiktivBeginnStufe1'),
                        placeholder: 'Kein fiktives Datum',
                        placeholderMode: 'hide',
                        clearable: true,
                      }),
                      history: historyFor('agLohnGehaltszahlungen.fiktivBeginnStufe1'),
                    })}
                    ${FormGridEntry({
                      label: 'Erfahrungsstufe',
                      render: ErfahrungsstufeText(props.contract, props.suffix),
                    })}

                    <div class="col-span-full grid grid-cols-[1fr_5fr] items-start gap-2">
                      <span class="text-sm font-semibold text-base-content/70">Weitere Zahlungen</span>
                      ${ZahlungenEditor(props)}
                    </div>
                  </div>
                `,
              })}

              <hr class="border-base-300" />
            `
      }
    </div>
  `;
});
